import type { SegmentExclusion } from "@/shared/api/generated/types/marketing"
import { UpcomingAppointmentExclusionScope } from "@/shared/api/generated/types/marketing"

export const DEFAULT_EXCLUSION_WINDOW_DAYS = 30

export const EMPTY_SEGMENT_EXCLUSION: SegmentExclusion = {
	enabled: false,
	noRepeat: false,
	windowDays: DEFAULT_EXCLUSION_WINDOW_DAYS,
	skipUpcomingAppointment: false,
	upcomingAppointmentScope: UpcomingAppointmentExclusionScope.any,
	upcomingSpecializationIds: [],
	upcomingResourceIds: [],
}

export function normalizeSegmentExclusion(
	exclusion?: SegmentExclusion,
): SegmentExclusion {
	if (!exclusion) {
		return { ...EMPTY_SEGMENT_EXCLUSION }
	}

	const noRepeat =
		exclusion.noRepeat ??
		Boolean(exclusion.enabled && !exclusion.skipUpcomingAppointment)

	return syncExclusionEnabled({
		...EMPTY_SEGMENT_EXCLUSION,
		...exclusion,
		noRepeat,
		windowDays: exclusion.windowDays ?? DEFAULT_EXCLUSION_WINDOW_DAYS,
		upcomingAppointmentScope:
			exclusion.upcomingAppointmentScope ??
			UpcomingAppointmentExclusionScope.any,
		upcomingSpecializationIds: exclusion.upcomingSpecializationIds ?? [],
		upcomingResourceIds: exclusion.upcomingResourceIds ?? [],
	})
}

export function syncExclusionEnabled(
	exclusion: SegmentExclusion,
): SegmentExclusion {
	return {
		...exclusion,
		enabled: Boolean(exclusion.noRepeat || exclusion.skipUpcomingAppointment),
	}
}

export function countActiveExclusions(exclusion: SegmentExclusion): number {
	let count = 0
	if (exclusion.noRepeat) {
		count += 1
	}
	if (exclusion.skipUpcomingAppointment) {
		count += 1
	}
	return count
}

export function estimateCountAfterExclusions(
	baseCount: number,
	exclusion: SegmentExclusion,
): number {
	let reduction = 0
	if (exclusion.noRepeat) {
		reduction += 0.05
	}
	if (exclusion.skipUpcomingAppointment) {
		reduction += 0.03
	}
	return Math.max(0, Math.round(baseCount * (1 - reduction)))
}

export function patchSegmentExclusion(
	exclusion: SegmentExclusion,
	patch: Partial<SegmentExclusion>,
): SegmentExclusion {
	return syncExclusionEnabled(
		normalizeSegmentExclusion({ ...exclusion, ...patch }),
	)
}
